import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import clsx from 'clsx'
import { makeStyles, useTheme } from '@mui/styles'
import { useMediaQuery } from '@mui/material'
import { cloneDeep, compact, omit, omitBy } from 'lodash'

import config from '@/config'
import { brandFontScope, brandHeading, brandText } from '@/utils/brand-typography'
import lang from '@/lang'
import ThemeInterface from '@/theme-interface'
import { brandOutlineButton, brandPrimaryButton } from '@/modules/base/components/modal/upgrade-modal-v2/_helpers/brandButtons'
import { ModalBig, ModalFullscreen } from '@/modules/base/components/modal'
import { ButtonSquare, CancelButton } from '@/modules/base/components/buttons'
import LoaderInline from '@/modules/base/components/loader/loader-inline'
import type { PeriodType, SubscriptionType, Type } from '@/redux/ducks/subscription/types/details'
import useBillingForm, { isBillingDataValid } from '@/modules/base/components/modal/upgrade-modal-v2/hooks/useBillingForm'
import useUpgradeSteps, { subscriptionSteps } from '@/modules/base/components/modal/upgrade-modal-v2/hooks/useUpgradeSteps'
import type { ISubscriptionSelectionItem } from '@/modules/base/components/modal/upgrade-modal-v2/types'
import usePurchase from '@/modules/base/components/modal/upgrade-modal-v2/hooks/usePurchase'
import SubscriptionService from '@/modules/base/components/subscription/subscriptionService'
import { Module, ModuleType } from '@/modules/moduleSubscription/types/module'
import type { CouponDataType } from '@/modules/base/components/modal/upgrade-modal/order-overview/summary/overview-summary'
import { trackOmoSubscription, trackOmoSubscriptionSelection } from '@/modules/base/helpers/omoSubscriptionTracker'
import { trackOrgamaxGaEvent } from '@/modules/base/helpers/trackOrgamaxGaEvent'
import WebStorageKey from '@/enums/web-storage-key.enum'
import WebstorageService from '@/modules/_services/webstorage.service'
import { subscriptionTypes as SUBSCRIPTION_TYPES } from '@/modules/base/util/constants'
import getIsOnlyFreeSelected from '../upgrade-modal/_helpers/getIsOnlyFreeSelected'
import { handleAddToCartForGtm } from '@/modules/moduleSubscription/components/_helpers/gtmHandlers'
import ConfirmEmailModal from '@/modules/account/components/confirm-email/confirm-email-modal'
import SubscriptionExpiredView from '../upgrade-modal/subscription-expired-view/subscription-expired-view'
import Header from '@/modules/base/components/modal/upgrade-modal-v2/header/header'
import UpgradeStepper from '@/modules/base/components/modal/upgrade-modal-v2/header/stepper/upgrade-stepper'
import SelectionSidebar from '@/modules/base/components/modal/upgrade-modal-v2/sidebar/selection-sidebar'
import SelectionDrawer from '@/modules/base/components/modal/upgrade-modal-v2/sidebar/selection-drawer'
import CheckoutContact from '@/modules/base/components/modal/upgrade-modal-v2/footer/checkout-contact'
import getCheckoutTitle from '@/modules/base/components/modal/upgrade-modal-v2/_helpers/getCheckoutTitle'
import getSelectionLines from '@/modules/base/components/modal/upgrade-modal-v2/_helpers/getSelectionLines'
import getIsCartEmpty from '@/modules/base/components/modal/upgrade-modal-v2/_helpers/isCartEmpty'
import CouponSection from '@/modules/base/components/modal/upgrade-modal-v2/sidebar/coupon-section'
import AddonsStep from '@/modules/base/components/modal/upgrade-modal-v2/steps/addons/addons-step'
import PlanStep from '@/modules/base/components/modal/upgrade-modal-v2/steps/plan/plan-step'
import BillingStep from '@/modules/base/components/modal/upgrade-modal-v2/steps/billing/billing-step'
import CancelCheckoutDialog from '@/modules/base/components/modal/upgrade-modal-v2/cancel-dialog/cancel-checkout-dialog'
import PurchaseSuccess from '@/modules/base/components/modal/upgrade-modal-v2/success/purchase-success'
import { selectionSteps, STEPS } from '@/redux/ducks/upgrade/wizard'
import { useAppSelector } from '@/redux/store'

const { agbUrl, privacyUrl } = config

const useStyles = makeStyles((theme: ThemeInterface) => ({
  // the checkout covers the whole page with its own background (Figma) - the dashboard is not visible
  dialog: {
    '& .MuiDialog-paper': {
      background: 'none',
    },
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    minHeight: '100vh',
  },
  // DO-13587: the checkout uses the typefaces of the new brand - Poppins for text, Sora for headings
  brandTypography: {
    ...brandText(theme, 'small'),
    ...brandFontScope(theme),
    color: theme.palette.brand.text.primary,
  },
  main: {
    flex: 1,
    padding: '44px 64px 64px',
    background: 'linear-gradient(180deg, #FFFFFF 0%, #EAF1FB 100%)',
    [theme.breakpoints.down('lg')]: {
      padding: '32px 24px 48px',
    },
    [theme.breakpoints.down(768)]: {
      padding: '20px 16px 32px',
    },
    fontSize: theme.brandTypography.text.regular.fontSize,
  },
  layout: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 900px) 380px',
    justifyContent: 'center',
    alignItems: 'start',
    gap: 32,
    maxWidth: 1312,
    margin: '0 auto',
    [theme.breakpoints.down('lg')]: {
      gridTemplateColumns: 'minmax(0, 1fr)',
      gap: 24,
    },
  },
  // "Plan wählen" has no sidebar - the content takes the full width and stays centred
  layoutWithoutSidebar: {
    gridTemplateColumns: 'minmax(0, 1fr)',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: 32,
    minWidth: 0,
  },
  // mobile: the step title moves from the header into the page
  pageTitle: {
    margin: '0 0 24px',
    ...brandHeading(theme, 'h6'),
    color: theme.palette.brand.text.heading,
  },
  sidebar: {
    position: 'sticky',
    top: 24,
    [theme.breakpoints.down('lg')]: {
      position: 'static',
    },
  },
  // mobile: the selection fills the full screen drawer, which brings its own frame
  sidebarInDrawer: {
    padding: '24px 16px',
    border: 'none',
    borderRadius: 0,
    boxShadow: 'none',
  },
  contact: {
    marginTop: 32,
  },
  card: {
    padding: 24,
    borderRadius: theme.borderRadiusExtraLarge,
    background: theme.palette.background.paper,
    boxShadow: theme.shadows[1],
    '& .billing-form__wrapper': {
      height: 'unset',
      padding: 0,
    },
  },
  stepActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 12,
    [theme.breakpoints.down(768)]: {
      flexDirection: 'column-reverse',
    },
  },
  pillButton: {
    width: 'auto',
    borderRadius: theme.borderRadiusPill,
    whiteSpace: 'nowrap',
    [theme.breakpoints.down(768)]: {
      width: '100%',
    },
  },
  sidebarButton: {
    ...brandPrimaryButton(theme),
  },
  primaryButton: {
    ...brandPrimaryButton(theme),
  },
  outlineButton: {
    ...brandOutlineButton(theme),
  },
  legalNotice: {
    margin: 0,
    color: theme.palette.brand.text.secondary,
    fontSize: theme.brandTypography.text.tiny.fontSize,
    '& a': {
      color: theme.palette.brand.main,
    },
  },
  processing: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 16,
    padding: '64px 24px',
    color: theme.palette.brand.text.secondary,
  },
  subsExpired: {
    padding: 0,
  },
}))

interface UpgradeModalProps {
  open: boolean
  onClose: () => void,
  onUpgraded?: () => void
  groupedSubscriptionTypes?: object,
  activeSubscriptions: SubscriptionType[]
  preSelectedSubscription?: SubscriptionType,
  preSelectedPeriod?: PeriodType,
  trialExpired: boolean
  includeModules?: boolean
  enableItemsFromStorage?: boolean
  showAllModules?: boolean
  preSelectedSubscriptionType?: Type
  addNextUpgradeToCart?: boolean
  modulesToAdd?: string[]
  // "Erweiterungen hinzufügen": a paid plan skips step 1 as if "Mit diesem Plan weiter" was clicked
  startAtAddons?: boolean
}

export default function UpgradeModal(props: UpgradeModalProps) {
  const {
    preSelectedSubscriptionType,
    activeSubscriptions = [],
    preSelectedPeriod,
    trialExpired = false,
    includeModules = false,
    enableItemsFromStorage = false,
    showAllModules = false,
    addNextUpgradeToCart = false,
    modulesToAdd = [],
    startAtAddons = false,
    onUpgraded,
    onClose,
  } = props
  let { preSelectedSubscription } = props
  const theme = useTheme<ThemeInterface>()
  // DO-13065: upgrade lock follows hasEmailAccess, the cosmetic hints stay on hasConfirmedEmail
  const { hasEmailAccess, firstName, lastName } = useAppSelector(state => state.user.info)
  // DO-13587: the phone layout follows the window width (below 768 px, like the login), not the device width
  const isMobile = useMediaQuery(theme.breakpoints.down(768))
  const isSidebarStacked = useMediaQuery(theme.breakpoints.down('lg'))
  const containerRef = useRef<HTMLDivElement>(null)

  const tenantRestores = useAppSelector(state => state.user.info.activeTenant.tenantRestores)
  if (!preSelectedSubscription && preSelectedSubscriptionType) {
    preSelectedSubscription = activeSubscriptions.find(sub => sub.typeOfSubscription === preSelectedSubscriptionType) as SubscriptionType
      || { typeOfSubscription: preSelectedSubscriptionType } as SubscriptionType
  }

  const subscriptionsReadyForSteps = preSelectedSubscription ? [preSelectedSubscription] : activeSubscriptions

  const [couponCode, setCouponCode] = useState<CouponDataType | null>(null)
  const [openEmailVerification, setOpenEmailVerification] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [moduleData, setModuleData] = useState<ModuleType | null>(null)
  const [skipAllModulesSelection, setSkipAllModulesSelection] = useState<boolean>(false)
  const [expiredUpgrade, setExpiredUpgrade] = useState<boolean>(false)
  const [openSubscriptionExpiredView, setOpenSubscriptionExpiredView] = useState<boolean>(true)
  const [isCartUpdated, setIsCartUpdated] = useState(false)
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false)
  // "Kauf abschließen" was clicked with data missing - the billing step names the missing fields
  const [showBillingErrors, setShowBillingErrors] = useState(false)
  // mobile: "Deine Auswahl" in full screen
  const [isSelectionOpen, setIsSelectionOpen] = useState(false)

  const {
    billingFormData,
    isIBANValid,
    setIsIBANValid,
    isFromAccount,
    handleBillingDataChange,
  } = useBillingForm({ activeSubscriptions: subscriptionsReadyForSteps })

  const {
    handlePurchase,
    productsInCart,
    addProductToCart,
    orderCompleted,
    completedOrder,
    selectedPeriod,
    setSelectedPeriod,
  } = usePurchase({ billingFormData, couponCode, setIsLoading, onUpgraded, preSelectedPeriod })

  const isOnlyFreeSelected = useMemo(() => {
    return getIsOnlyFreeSelected(productsInCart)
  }, [productsInCart])

  // derived, not stored: a plan change after the billing step (FREE <-> paid) must re-evaluate
  // whether bank details are required, even without any input in the form
  const isBillingFormValid = useMemo(
    () => isBillingDataValid(billingFormData, isOnlyFreeSelected, isIBANValid),
    [billingFormData, isOnlyFreeSelected, isIBANValid],
  )

  // "Weiter zu Daten & Zahlung" and the purchase stay disabled until something is selected
  const isCartEmpty = useMemo(() => getIsCartEmpty(productsInCart), [productsInCart])
  const selectionCount = useMemo(() => getSelectionLines(productsInCart, selectedPeriod).length, [productsInCart, selectedPeriod])

  const handleSkipModuleSelection = useCallback(async() => {
    const subscriptionsInCart = await Promise.all(compact(Object.keys(productsInCart).map(productType => {
      const moduleProducts = Object.values(productsInCart[productType]).filter(sub => !sub.type)
      // if modules are already added in the cart, we skip the module selection of that subscription
      if (moduleProducts.length) return null

      return Object.values(productsInCart[productType]).find(item => item.type)?.name
    })).map(getModuleData))

    const subscriptionsWithModules = subscriptionsInCart.some(sub => sub.availableModules?.length)
    setSkipAllModulesSelection(!subscriptionsWithModules)
  }, [productsInCart])

  useEffect(() => {
    if (showAllModules && !isCartEmpty) handleSkipModuleSelection()
  }, [productsInCart, showAllModules, isCartEmpty])

  const {
    activeStep,
    stepsInOrder,
    goToNextStep,
    handleCancel,
    handlePrevious,
    subscriptionTypes,
  } = useUpgradeSteps({
    activeSubscriptions: subscriptionsReadyForSteps,
    onClose,
    showAllModules,
    skipAllModulesSelection,
    isCartUpdated,
  })
  const subscriptionTypeTypesLabel = subscriptionsReadyForSteps?.map(s => s.typeOfSubscription).join(', ') || ''
  const isSelectionStep = selectionSteps.includes(activeStep)
  const activeStepIndex = stepsInOrder.indexOf(activeStep)
  const classes = useStyles()

  // manual cancel or close clicked
  const onCancel = () => {
    setCancelDialogOpen(false)
    handleCancel()
    window.history.back()
  }
  const handleClose = () => {
    onClose()
    window.history.back()
  }

  useEffect(() => {
    window.history.pushState({ modal: true }, '')

    // history back was used
    const windowBackButtonHandler = () => {
      handleCancel()
    }
    window.addEventListener('popstate', windowBackButtonHandler)
    return () => {
      window.removeEventListener('popstate', windowBackButtonHandler)
    }
  }, [])

  useEffect(() => {
    if (preSelectedSubscriptionType && addNextUpgradeToCart && subscriptionTypes.length) {
      let nextSub
      let period = 'year'
      const currentSub = activeSubscriptions.find(sub => sub.typeOfSubscription === preSelectedSubscriptionType)
      const excludedUpgradeSubList = [SUBSCRIPTION_TYPES.FREE_2025]
      const allowedSubscriptons = subscriptionTypes.filter(sub => !excludedUpgradeSubList.includes(sub.name))

      if (currentSub.type === SUBSCRIPTION_TYPES.TRIAL) {
        nextSub = allowedSubscriptons[0]
      } else {
        nextSub = allowedSubscriptons[allowedSubscriptons.findIndex(sub => sub.name === currentSub.type) + 1] || allowedSubscriptons[0]
        period = currentSub.period
      }
      if (nextSub) handleSubscriptionSelect(nextSub, period)
    }
  }, [preSelectedSubscriptionType, addNextUpgradeToCart, subscriptionTypes])

  // Auto-add specified modules after moduleData is loaded
  useEffect(() => {
    if (!modulesToAdd.length || !moduleData?.availableModules?.length) return

    const subscriptionType = moduleData.type as Type
    modulesToAdd.forEach(moduleName => {
      // Skip if already in cart
      if (productsInCart?.[subscriptionType]?.[moduleName]) return

      const module = moduleData.availableModules.find(m => m.name === moduleName)
      if (!module) return

      const priceData = module.prices?.find(p => p.period === selectedPeriod)
      if (priceData?.values?.[0]) {
        const moduleToAdd: Module = {
          ...module,
          paymentInterval: {
            period: selectedPeriod,
            ...priceData.values[0],
          },
          amount: 1,
          paymentIntervalOfSubscription: selectedPeriod,
          period: selectedPeriod,
        }
        addToCart(subscriptionType, moduleToAdd, 1)
      }
    })
  }, [moduleData, modulesToAdd, selectedPeriod])

  useEffect(() => {
    setOpenEmailVerification(!hasEmailAccess)

    if (enableItemsFromStorage) {
      const shoppingCart = WebstorageService.getItem(WebStorageKey.SHOPPING_CART)
      if (shoppingCart?.items && Object.keys(shoppingCart?.items).length) {
        addProductToCart(shoppingCart?.items)
      }
    }
  }, [])

  useEffect(() => {
    setIsSelectionOpen(false)
    if (containerRef.current) {
      containerRef.current.scrollIntoView?.()
    }
  }, [activeStep, orderCompleted])

  useEffect(() => {
    if (preSelectedSubscription && !preSelectedSubscription?.isTrial && preSelectedSubscription?.paymentInterval) {
      setSelectedPeriod(preSelectedSubscription.paymentInterval)
    }
  }, [preSelectedSubscription])

  const getModuleData = async(name: string) => {
    const response = await SubscriptionService.moduleSubscriptions.getModulesOfSubscriptionType(name)
    const data = response?.data
    return data
  }

  // existing customer: "Mit diesem Plan weiter" keeps the current plan - nothing is bought for it, the
  // add-ons are booked for it. A plan chosen before (after going back) leaves the cart with its add-ons.
  const handleContinueWithCurrent = (subscriptionType: Type, period: string) => {
    // the add-ons are billed like a paid current plan (the API books them with its interval) - otherwise
    // with the period of the switch in step 1, so the prices and the hint of "Deine Auswahl" follow it
    const currentSubscription = activeSubscriptions.find(sub => sub.typeOfSubscription === subscriptionType)
    const currentPeriod = currentSubscription && !currentSubscription.isTrial ? currentSubscription.paymentInterval : null
    const nextPeriod = (currentPeriod || period) as PeriodType
    // a coupon is validated for one interval - it must be redeemed again
    if (couponCode && nextPeriod !== selectedPeriod) setCouponCode(null)
    setSelectedPeriod(nextPeriod)

    const hasOtherPlan = Object.values(productsInCart[subscriptionType] || {}).some(item => item.type)
    if (hasOtherPlan) {
      addProductToCart(omit(productsInCart, subscriptionType))
      setCouponCode(null)
      setIsCartUpdated(true)
    }
    // the add-ons step loads the add-ons of the current plan itself
    setModuleData(null)
    goToNextStep(null, false, includeModules)
  }

  // once on opening: a trial has no plan to keep, so it stays at step 1 - "Zurück" leads there as well
  const isStartAtAddonsHandled = useRef(false)
  useEffect(() => {
    if (!startAtAddons || isStartAtAddonsHandled.current || !subscriptionSteps.includes(activeStep)) return
    isStartAtAddonsHandled.current = true
    const currentSubscription = activeSubscriptions.find(sub => sub.typeOfSubscription === activeStep)
    if (currentSubscription && !currentSubscription.isTrial) {
      handleContinueWithCurrent(activeStep as Type, selectedPeriod)
    }
  }, [activeStep])

  const handleSubscriptionSelect = async(subscription: ISubscriptionSelectionItem, period: string) => {
    const { prices, name } = subscription
    // the plan list (wizard store) types `type` as a plain string
    const type = subscription.type as Type
    const selectedPayInterval = prices.find(price => price.period === period)
    const paymentInterval = { period, ...selectedPayInterval.values[0] }
    const previousPlanName = Object.values(productsInCart[type] || {}).find(item => item.type)?.name
    const isPlanChanged = Boolean(previousPlanName) && previousPlanName !== name
    // DO-13587: the payment interval is chosen in step 1 only, so the latest choice always wins
    setSelectedPeriod(period as PeriodType)

    // a coupon is validated for one plan and interval - it must be redeemed again after going back
    if (couponCode && (isPlanChanged || period !== selectedPeriod)) {
      setCouponCode(null)
    }

    // the add-ons of the new plan, loaded before the cart changes so the ones it does not offer are dropped
    const newModuleData = includeModules ? await getModuleData(name) : null

    const module: Module = {
      ...subscription,
      paymentInterval,
      bookable: null,
      bookableCount: null,
      owned: null,
      otherSubscriptions: null,
      singleton: true,
      amount: 1,
      paymentIntervalOfSubscription: selectedPayInterval.period,
      period: selectedPayInterval.period,
      isProductItem: true,
    }
    trackOmoSubscriptionSelection(subscription)
    // going back keeps the cart, so a newly chosen plan replaces the one selected before - together
    // with the add-ons the new plan cannot be booked with
    const isAddonKept = (addonName: string) => !isPlanChanged
      || Boolean(newModuleData?.availableModules?.some(addon => addon.name === addonName))
    addToCart(type, module, module.amount, true, isAddonKept)

    if (includeModules) {
      setModuleData(newModuleData)
    }

    goToNextStep(null, false, includeModules)
  }

  const addToCart = (type: Type, module: Module, quantity: number, replacePlan = false, isAddonKept: (addonName: string) => boolean = () => true) => {
    if (!module) return

    const itemsOfType = replacePlan
      ? omitBy(productsInCart[type], item => item.type ? item.name !== module.name : !isAddonKept(item.name))
      : productsInCart[type]
    const newItem = {
      [type]: {
        ...itemsOfType,
        [module.name]: {
          ...module,
          count: quantity,
        },
      },
    }

    const updatedCart = { ...productsInCart, ...newItem }
    addProductToCart(updatedCart)
    setIsCartUpdated(true)
    handleAddToCartForGtm(updatedCart[type][module.name], quantity + 1, type, cloneDeep(updatedCart))
  }

  const handleModuleDelete = (type: string, moduleName: string) => {
    addProductToCart({
      ...productsInCart,
      [type]: omit(productsInCart[type], moduleName),
    })
    setIsCartUpdated(true)
  }

  const renderStepContent = () => {
    switch (activeStep) {
      case STEPS.SUBSCRIPTION_MAIN:
      case STEPS.SUBSCRIPTION_DOCUMENT:
      case STEPS.SUBSCRIPTION_STAFF: {
        const activeStepSub = subscriptionsReadyForSteps.find(sub => sub.typeOfSubscription === activeStep)
        return (
          <PlanStep
            subscriptionType={activeStepSub.typeOfSubscription}
            plans={subscriptionTypes}
            items={productsInCart}
            period={selectedPeriod}
            onSelect={handleSubscriptionSelect}
            onContinueWithCurrent={(period) => handleContinueWithCurrent(activeStepSub.typeOfSubscription as Type, period)}
          />
        )
      }

      case STEPS.EXTENSIONS:
        return (
          <AddonsStep
            items={productsInCart}
            period={selectedPeriod}
            activeSubscriptions={activeSubscriptions}
            moduleData={moduleData}
            onChange={addToCart}
            onRemove={handleModuleDelete}
          />
        )

      case STEPS.BILLING: return (
        <BillingStep
          data={billingFormData}
          hideBankDetails={isOnlyFreeSelected}
          isFromAccount={isFromAccount}
          showErrors={showBillingErrors}
          onIBANChange={setIsIBANValid}
          onChange={handleBillingDataChange}
        />
      )
    }
  }

  if (!hasEmailAccess) {
    return (
      <ConfirmEmailModal
        closeModal={() => setOpenEmailVerification(false)}
        open={openEmailVerification}
        isModal
        gaData={{
          eventCategory: 'DOI',
          eventAction: 'Jetzt kaufen',
          eventLabel: `DOI durchgeführt ${subscriptionTypeTypesLabel}`,
        }}
        title={lang.invoiceDetail.confirmEmailTitle}
      />
    )
  }

  if (trialExpired && !expiredUpgrade) {
    const readOnlyConfirmed = WebstorageService.getItem(WebStorageKey.READ_ONLY_CONFIRMED)
    if (!readOnlyConfirmed) {
      return (
        <ModalBig width={1050} open={openSubscriptionExpiredView} showCloseIcon={false} className={classes.subsExpired}>
          <SubscriptionExpiredView onUpgradeClick={() => {
            setExpiredUpgrade(true)
          }} onAbort={() => {
            trackOmoSubscription(
              'click_testphase',
              'testphase_lesemodus',
            )
            setOpenSubscriptionExpiredView(false)
          }} firstName={firstName} lastName={lastName} />
        </ModalBig>
      )
    }
    return null
  }

  const restoreTokenAvailable = !!/restore\/?.*/.test(window.location.pathname) || !!tenantRestores?.length
  if (restoreTokenAvailable) {
    return null
  }

  const wizardTexts = lang.upgrade.wizard
  const isBillingStep = activeStep === STEPS.BILLING
  // DO-13587: a coupon can be redeemed from "Add-ons" on - the AGB notice stays with the purchase in "Daten & Zahlung"
  const canRedeemCoupon = activeStep === STEPS.EXTENSIONS || isBillingStep
  const isPurchaseDisabled = isLoading || isCartEmpty

  // the step's primary action - shown below the content and in the sidebar
  const primaryAction = (() => {
    if (activeStep === STEPS.EXTENSIONS) {
      return { label: wizardTexts.buttons.toBilling, onClick: () => goToNextStep(null), disabled: isCartEmpty, dataQsId: 'checkout-to-billing' }
    }
    if (isBillingStep) {
      const onPurchaseClick = () => {
        // DO-13587: the button is always active - missing data is named at the fields instead
        if (!isBillingFormValid) {
          setShowBillingErrors(true)
          setIsSelectionOpen(false)
          containerRef.current?.scrollIntoView?.()
          return
        }
        // same event as the former order overview, the marketing reports rely on its labels
        trackOrgamaxGaEvent('purchase', 'Bestellen', 'Bestellübersicht')
        handlePurchase()
      }
      // a FREE plan is not bought - it keeps the wording of the former order overview
      const label = isOnlyFreeSelected ? lang.upgradeModal.orderNowOnFree : wizardTexts.buttons.purchase
      return { label, onClick: onPurchaseClick, disabled: isPurchaseDisabled, dataQsId: 'checkout-purchase' }
    }
    return null
  })()

  const legalTexts = wizardTexts.sidebar.legalNotice
  const legalNotice = isBillingStep && (
    <p className={classes.legalNotice}>
      {legalTexts.prefix}
      <a href={agbUrl} rel='noopener noreferrer' target='_blank'>{legalTexts.agb}</a>
      {legalTexts.and}
      <a href={privacyUrl} rel='noopener noreferrer' target='_blank'>{legalTexts.privacy}</a>
      {legalTexts.suffix}
    </p>
  )

  // DO-13587: nothing is lost in "Plan wählen" yet, so cancelling there needs no confirmation
  const requestCancel = () => {
    if (isSelectionStep) {
      onCancel()
      return
    }
    setCancelDialogOpen(true)
  }

  // DO-13587: no "Schritt überspringen" - an existing customer goes on with the current plan instead
  const stepActions = (activeStepIndex > 0 || primaryAction) && (
    <div className={classes.stepActions}>
      {activeStepIndex > 0 && (
        <CancelButton className={clsx(classes.pillButton, classes.outlineButton)} label={wizardTexts.buttons.back} dataQsId='checkout-back' onClick={handlePrevious} />
      )}
      {primaryAction && (
        <ButtonSquare
          className={clsx(classes.pillButton, classes.primaryButton)}
          label={primaryAction.label}
          disabled={primaryAction.disabled}
          dataQsId={`${primaryAction.dataQsId}-bottom`}
          onClick={primaryAction.onClick}
        />
      )}
    </div>
  )

  // the plan cards already show their prices, the selection is listed from the add-ons on
  const showSidebar = !isSelectionStep
  // mobile: the selection opens in full screen from a FAB in the header - with the step's action, like on desktop
  const showActionInSidebar = !isSidebarStacked || isMobile
  const sidebar = showSidebar && (
    <SelectionSidebar
      className={isMobile ? classes.sidebarInDrawer : classes.sidebar}
      items={productsInCart}
      period={selectedPeriod}
      couponCode={couponCode}
      showTitle={!isMobile}
      couponSlot={canRedeemCoupon && !isCartEmpty && (
        <CouponSection items={productsInCart} period={selectedPeriod} onCouponCodeChange={setCouponCode} />
      )}
      onCouponRemove={canRedeemCoupon ? () => setCouponCode(null) : undefined}
      action={primaryAction && showActionInSidebar && (
        <ButtonSquare
          className={classes.sidebarButton}
          label={primaryAction.label}
          disabled={primaryAction.disabled}
          dataQsId={primaryAction.dataQsId}
          onClick={() => {
            setIsSelectionOpen(false)
            primaryAction.onClick()
          }}
        />
      )}
      footer={showActionInSidebar && legalNotice}
    />
  )
  const selectionDrawer = isMobile && showSidebar && !orderCompleted && (
    <SelectionDrawer
      open={isSelectionOpen}
      count={selectionCount}
      onOpen={() => setIsSelectionOpen(true)}
      onClose={() => setIsSelectionOpen(false)}>
      {sidebar}
    </SelectionDrawer>
  )
  const isSidebarInline = isSidebarStacked && !isMobile

  const renderCheckout = () => {
    if (isLoading && isBillingStep) {
      return (
        <div className={clsx(classes.card, classes.processing)} data-qs-id='checkout-processing'>
          <LoaderInline />
          {wizardTexts.processing}
        </div>
      )
    }

    return (
      <div className={clsx(classes.layout, !showSidebar && classes.layoutWithoutSidebar)}>
        <div className={classes.content}>
          <UpgradeStepper />
          {renderStepContent()}
          {isSidebarInline && sidebar}
          {stepActions}
          {isSidebarStacked && legalNotice}
        </div>
        {!isSidebarStacked && sidebar}
      </div>
    )
  }

  return (
    <ModalFullscreen
      {...props}
      className={classes.dialog}
      onClose={requestCancel}
      disableBackdropClick
      disableEscapeKeyDown
      dataQsId='upgradeModal'>
      <div className={clsx(classes.container, classes.brandTypography)} ref={containerRef}>
        {/* no cancelling while the order is processed */}
        <Header onCancel={isLoading ? undefined : requestCancel} selectionAction={selectionDrawer} />
        <main className={classes.main}>
          {isMobile && (
            <h1 className={classes.pageTitle} data-qs-id='checkout-page-title'>
              {getCheckoutTitle(activeStep, Boolean(orderCompleted && completedOrder))}
            </h1>
          )}
          {orderCompleted && completedOrder
            ? <PurchaseSuccess order={completedOrder} onFinish={handleClose} />
            : renderCheckout()}
          <CheckoutContact className={classes.contact} />
        </main>
      </div>
      <CancelCheckoutDialog
        open={cancelDialogOpen}
        onCancelPurchase={onCancel}
        onContinue={() => setCancelDialogOpen(false)}
      />
    </ModalFullscreen>
  )
}
